/**
 * POS Sales Summary Report Screen
 * Uses Report Engine for consistent layout and export
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Receipt, Calendar, TrendingUp, TrendingDown, DollarSign, Users, Package } from 'lucide-react';
import { ReportLayout } from '@/components/report-engine';
import api from '@/services/api';

const POSSalesSummaryReport = ({ theme }) => {
  const isDark = theme === 'dark';
  
  // Data state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [counterFilter, setCounterFilter] = useState('');
  const [shiftFilter, setShiftFilter] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Summary state
  const [totalSales, setTotalSales] = useState(0);
  const [totalReturns, setTotalReturns] = useState(0);
  const [netSales, setNetSales] = useState(0);
  const [totalBills, setTotalBills] = useState(0);
  const [avgBillValue, setAvgBillValue] = useState(0);

  useEffect(() => {
    fetchSalesData();
  }, [fromDate, toDate, page, pageSize, counterFilter, shiftFilter]);

  const fetchSalesData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use POS_SALES_SUMMARY report code
      const response = await api.post('/api/report-engine/data/POS_SALES_SUMMARY', {
        page,
        pageSize,
        filters: { 
          fromDate, 
          toDate, 
          search: searchQuery || undefined,
          counter: counterFilter || undefined,
          shift: shiftFilter || undefined
        }
      });

      if (response.data.success) {
        const rows = response.data.data?.rows || [];
        const paginationData = response.data.data;
        
        setData(rows);
        setTotalRows(paginationData.total || rows.length);
        setTotalPages(paginationData.totalPages || 1);

        // Calculate totals
        const totSales = rows.reduce((sum, r) => sum + (parseFloat(r.GrossAmount) || 0), 0);
        const totReturns = rows.reduce((sum, r) => sum + (parseFloat(r.ReturnAmount) || 0), 0);
        setTotalSales(totSales);
        setTotalReturns(totReturns);
        setNetSales(totSales - totReturns);
        setTotalBills(rows.length);
        setAvgBillValue(rows.length > 0 ? (totSales - totReturns) / rows.length : 0);
      } else {
        // Use mock data for demonstration
        const mockData = generateMockData();
        setData(mockData);
        calculateSummary(mockData);
      }
    } catch (err) {
      console.error('Error fetching POS sales data:', err);
      // Use mock data on error
      const mockData = generateMockData();
      setData(mockData);
      calculateSummary(mockData);
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    const counters = ['Counter 1', 'Counter 2', 'Counter 3'];
    const shifts = ['Morning', 'Evening', 'Night'];
    const salesmen = ['Rajesh', 'Priya', 'Amit', 'Sanjay'];
    
    return Array.from({ length: 20 }, (_, i) => ({
      SlNo: i + 1,
      BillNo: `POS/2024/${String(1000 + i).padStart(4, '0')}`,
      BillDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      Counter: counters[Math.floor(Math.random() * counters.length)],
      Shift: shifts[Math.floor(Math.random() * shifts.length)],
      Salesman: salesmen[Math.floor(Math.random() * salesmen.length)],
      CustomerName: `Customer ${i + 1}`,
      ItemCount: Math.floor(Math.random() * 10) + 1,
      GrossAmount: (Math.random() * 10000 + 500).toFixed(2),
      Discount: (Math.random() * 500).toFixed(2),
      Tax: (Math.random() * 500).toFixed(2),
      NetAmount: 0,
      PaymentMode: ['Cash', 'Card', 'UPI'][Math.floor(Math.random() * 3)],
      ReturnAmount: Math.random() > 0.9 ? (Math.random() * 500).toFixed(2) : '0.00'
    })).map(row => ({
      ...row,
      NetAmount: (parseFloat(row.GrossAmount) - parseFloat(row.Discount) + parseFloat(row.Tax)).toFixed(2)
    }));
  };

  const calculateSummary = (rows) => {
    const totSales = rows.reduce((sum, r) => sum + parseFloat(r.NetAmount || 0), 0);
    const totReturns = rows.reduce((sum, r) => sum + parseFloat(r.ReturnAmount || 0), 0);
    setTotalSales(totSales);
    setTotalReturns(totReturns);
    setNetSales(totSales - totReturns);
    setTotalBills(rows.length);
    setAvgBillValue(rows.length > 0 ? (totSales - totReturns) / rows.length : 0);
    setTotalRows(rows.length);
    setTotalPages(1);
  };

  const handleRefresh = useCallback(() => {
    fetchSalesData();
  }, [fromDate, toDate, page, pageSize, counterFilter, shiftFilter]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // Column definitions for Report Engine
  const columns = [
    { key: 'SlNo', label: 'Sl.No', width: 60, align: 'center' },
    { key: 'BillNo', label: 'Bill No', width: 140, sortable: true },
    { key: 'BillDate', label: 'Date', width: 100, sortable: true },
    { key: 'Counter', label: 'Counter', width: 100 },
    { key: 'Shift', label: 'Shift', width: 80 },
    { key: 'Salesman', label: 'Salesman', width: 100 },
    { key: 'CustomerName', label: 'Customer', width: 120 },
    { key: 'ItemCount', label: 'Items', width: 60, align: 'center' },
    { key: 'GrossAmount', label: 'Gross Amt', width: 100, align: 'right', format: 'currency' },
    { key: 'Discount', label: 'Discount', width: 80, align: 'right', format: 'currency' },
    { key: 'Tax', label: 'Tax', width: 80, align: 'right', format: 'currency' },
    { key: 'NetAmount', label: 'Net Amt', width: 110, align: 'right', format: 'currency' },
    { key: 'PaymentMode', label: 'Payment', width: 80, align: 'center' },
    { key: 'ReturnAmount', label: 'Returns', width: 90, align: 'right', format: 'currency' },
  ];

  // Summary cards
  const summaryCards = [
    {
      title: 'Total Sales',
      value: formatCurrency(totalSales),
      icon: <TrendingUp size={20} className="text-green-500" />,
      color: 'green'
    },
    {
      title: 'Total Returns',
      value: formatCurrency(totalReturns),
      icon: <TrendingDown size={20} className="text-red-500" />,
      color: 'red'
    },
    {
      title: 'Net Sales',
      value: formatCurrency(netSales),
      icon: <DollarSign size={20} className="text-blue-500" />,
      color: 'blue'
    },
    {
      title: 'Total Bills',
      value: totalBills,
      icon: <Receipt size={20} className="text-purple-500" />,
      color: 'purple'
    },
    {
      title: 'Avg Bill Value',
      value: formatCurrency(avgBillValue),
      icon: <Package size={20} className="text-orange-500" />,
      color: 'orange'
    }
  ];

  // Filters component
  const FiltersSection = () => (
    <div className="flex flex-wrap gap-3 items-end">
      <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>From Date</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className={`px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`}
        />
      </div>
      <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>To Date</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className={`px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`}
        />
      </div>
      <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Counter</label>
        <select
          value={counterFilter}
          onChange={(e) => setCounterFilter(e.target.value)}
          className={`px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="">All Counters</option>
          <option value="Counter 1">Counter 1</option>
          <option value="Counter 2">Counter 2</option>
          <option value="Counter 3">Counter 3</option>
        </select>
      </div>
      <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Shift</label>
        <select
          value={shiftFilter}
          onChange={(e) => setShiftFilter(e.target.value)}
          className={`px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="">All Shifts</option>
          <option value="Morning">Morning</option>
          <option value="Evening">Evening</option>
          <option value="Night">Night</option>
        </select>
      </div>
    </div>
  );

  return (
    <ReportLayout
      title="POS Sales Summary"
      subtitle="Daily sales transaction report"
      icon={<Receipt size={20} />}
      theme={theme}
      columns={columns}
      data={data}
      loading={loading}
      error={error}
      reportCode="POS_SALES_SUMMARY"
      summaryCards={summaryCards}
      filtersComponent={<FiltersSection />}
      pagination={{
        page,
        pageSize,
        totalRows,
        totalPages,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange
      }}
      onRefresh={handleRefresh}
      exportFileName={`POS_Sales_Summary_${fromDate}_${toDate}`}
    />
  );
};

export default POSSalesSummaryReport;
